<template>
  <DashboardSection
    title="Mediaplan-Timeline"
    :subtitle="subtitle"
    :empty="!rows.length"
    empty-text="Keine Print-Schaltungen im Zeitraum."
  >
    <div class="mb-cal">
      <!-- UX: Die Doppel-Codierung wird jetzt erklärt: Kartenfarbe = Schaltungstyp, Punkt = Status. -->
      <div class="mb-cal__legend">
        <div class="mb-cal__legend-group">
          <span class="mb-cal__legend-caption">Kartenfarbe = Schaltungstyp</span>
          <PlacementTypeLegend :items="placementTypes" :descriptions="placementDescriptions" inline />
        </div>
        <div class="mb-cal__legend-group">
          <span class="mb-cal__legend-caption">Punkt = Status</span>
          <span v-for="item in statusLegend" :key="item.code" class="mb-cal__status" :class="item.code">
            <i :class="['mb-status-dot', item.code]" aria-hidden="true" />
            {{ item.label }}
          </span>
        </div>
      </div>
      <div class="mb-cal__scroll" tabindex="0" role="region" aria-label="Mediaplan-Timeline, horizontal scrollbar">
        <table class="mb-cal__grid" :class="`mb-cal__grid--${granularity}`">
          <thead>
            <tr>
              <th class="mb-cal__sticky" :rowspan="granularity === 'month' ? 1 : 2">Titel</th>
              <th
                v-for="group in headerGroups"
                :key="group.key"
                class="mb-cal__month"
                :colspan="group.span"
                :title="group.label"
              >
                {{ group.span > 1 || granularity === 'month' ? group.label : group.short }}
              </th>
            </tr>
            <tr v-if="granularity !== 'month'">
              <th v-for="column in columns" :key="`d-${column.key}`" class="mb-cal__day">
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.title">
              <!-- UX: Zeilentitel ausgeschrieben ("Apotheken Umschau" statt "AU"), wie in der Tabelle. -->
              <th class="mb-cal__sticky mb-cal__title" :title="row.short !== row.title ? `Kürzel: ${row.short}` : undefined">
                {{ row.title }}
              </th>
              <td v-for="column in columns" :key="column.key" class="mb-cal__cell">
                <div
                  v-for="event in row.cells[column.key] || []"
                  :key="event.id"
                  class="mb-cal__event"
                  :class="[event.placement_type, { 'is-cancelled': event.status === 'storniert' }]"
                  :title="event.tooltip"
                >
                  <!-- UX: Eine Zeile "12.9. · 09/2026", Format darunter – statt Umbruch auf 4 Zeilen. -->
                  <span class="mb-cal__when">
                    <i :class="['mb-status-dot', event.status]" aria-hidden="true" />
                    {{ event.when }}<template v-if="event.issue"> · {{ event.issue }}</template>
                  </span>
                  <span v-if="event.format" class="mb-cal__issue">{{ event.format }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </DashboardSection>
</template>

<script setup>
import { computed } from 'vue';
import {
  MONTH_NAMES,
  addDays,
  endOfMonth,
  formatDate,
  formatDateShort,
  isoDate,
  isoWeekNumber,
  parseDate,
  startOfMonth,
  startOfWeek
} from '../composables/formatters';
import DashboardSection from './DashboardSection.vue';
import PlacementTypeLegend from './PlacementTypeLegend.vue';

const STATUS_LABELS = [
  { code: 'geplant', label: 'geplant' },
  { code: 'angefragt', label: 'angefragt' },
  { code: 'bestaetigt', label: 'bestätigt' },
  { code: 'erschienen', label: 'erschienen' },
  { code: 'verschoben', label: 'verschoben' },
  { code: 'storniert', label: 'storniert' }
];

const STATUS_NAME = Object.fromEntries(STATUS_LABELS.map((item) => [item.code, item.label]));

// UX: Bis zu diesem Datenzeitraum Kalenderwochen, darüber nur Monate mit Schaltungen.
const WEEK_VIEW_MAX_DAYS = 120;

const props = defineProps({
  events: { type: Array, default: () => [] },
  // UX: id → voller Titel (aus der Detailtabelle), damit die Zeile wie die Tabelle heißt.
  titleNames: { type: Object, default: () => ({}) },
  placementTypes: { type: Array, default: () => [] },
  placementDescriptions: { type: Object, default: null }
});

const statusLegend = STATUS_LABELS;

const dated = computed(() => (props.events || []).filter((item) => parseDate(item.date)));

// UX: Nur den Zeitraum mit Daten zeigen (vorher: 12 Monate, davon 9 leer).
const range = computed(() => {
  const eventTimes = dated.value.map((item) => parseDate(item.date).getTime());
  if (!eventTimes.length) return null;
  return { start: new Date(Math.min(...eventTimes)), end: new Date(Math.max(...eventTimes)) };
});

const dayCount = computed(() => {
  if (!range.value) return 0;
  return Math.round((range.value.end - range.value.start) / 86400000) + 1;
});

const granularity = computed(() => (dayCount.value > WEEK_VIEW_MAX_DAYS ? 'month' : 'week'));

const subtitle = computed(() => {
  if (!range.value) return '';
  const unit = granularity.value === 'month' ? 'Monate mit Schaltungen' : 'Kalenderwochen im Schaltungszeitraum';
  return `${unit} · ${formatDate(range.value.start)} – ${formatDate(range.value.end)}`;
});

const columns = computed(() => {
  if (!range.value) return [];
  const { start, end } = range.value;
  const cols = [];

  if (granularity.value === 'month') {
    // Nur Monate, in denen tatsächlich etwas erscheint.
    const months = new Set(dated.value.map((item) => {
      const date = parseDate(item.date);
      return `${date.getFullYear()}-${date.getMonth()}`;
    }));
    let cursor = startOfMonth(start);
    while (cursor && cursor <= end) {
      const key = `${cursor.getFullYear()}-${cursor.getMonth()}`;
      if (months.has(key)) {
        cols.push({
          key,
          start: cursor,
          end: endOfMonth(cursor),
          label: `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`
        });
      }
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return cols;
  }

  let cursor = startOfWeek(start);
  while (cursor && cursor <= end) {
    const weekEnd = addDays(cursor, 6);
    cols.push({
      key: isoDate(cursor),
      start: cursor,
      end: weekEnd,
      label: `KW ${isoWeekNumber(cursor)}`
    });
    cursor = addDays(cursor, 7);
  }
  return cols;
});

const headerGroups = computed(() => {
  if (granularity.value === 'month') {
    return columns.value.map((column) => ({ key: column.key, span: 1, label: column.label, short: column.label }));
  }
  const groups = [];
  columns.value.forEach((column) => {
    const key = `${column.start.getFullYear()}-${column.start.getMonth()}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.span += 1;
      return;
    }
    groups.push({
      key,
      span: 1,
      label: `${MONTH_NAMES[column.start.getMonth()]} ${column.start.getFullYear()}`,
      // UX: Bei nur einer Wochenspalte die Kurzform, damit der Kopf nicht in den Nachbarmonat läuft.
      short: `${MONTH_NAMES[column.start.getMonth()].slice(0, 3)} ${String(column.start.getFullYear()).slice(2)}`
    });
  });
  return groups;
});

function columnForDate(date) {
  const time = parseDate(date)?.getTime();
  if (time == null) return null;
  return columns.value.find((column) => {
    const start = column.start.getTime();
    const end = column.end.getTime() + 86399999;
    return time >= start && time <= end;
  });
}

const rows = computed(() => {
  if (!columns.value.length) return [];
  const grouped = {};
  dated.value.forEach((item) => {
    const short = item.title || 'Ohne Titel';
    const title = props.titleNames[String(item.id)] || short;
    if (!grouped[title]) grouped[title] = { short, cells: {} };
    const column = columnForDate(item.date);
    if (!column) return;
    const cells = grouped[title].cells;
    if (!cells[column.key]) cells[column.key] = [];
    cells[column.key].push({
      id: item.id,
      when: formatDateShort(item.date),
      issue: item.issue || '',
      format: item.format || '',
      placement_type: item.placement_type || '',
      status: item.status || '',
      tooltip: [
        title,
        formatDate(item.date),
        item.issue ? `Ausgabe ${item.issue}` : '',
        item.format,
        STATUS_NAME[item.status] ? `Status: ${STATUS_NAME[item.status]}` : item.status
      ].filter(Boolean).join(' · ')
    });
  });
  return Object.entries(grouped).map(([title, row]) => ({ title, short: row.short, cells: row.cells }));
});
</script>
